import Backbone from 'backbone'
import _ from 'lodash'
import bb from 'bounding-box'
import { SnapType } from './support/snap'
import { SnapGrid as GRID } from './support/snap'
import Box from './support/box'

var Connector = Backbone.Model.extend({
  defaults: {
    style: {
      'stroke-width': 2,
      'stroke-dasharray': '',
      'opacity': 1
    }
  }
})

Connector.STROKE_DASHARRAY = ['', '8 4', '2 2'] // Defaults: Solid, Medium, Small
// We scale these based on the stroke width

Connector.OPACITY = [1, .75, .5, .25]

Connector.STROKE_WIDTH = [2, 4, 6, 8]

var Connectors = Backbone.Collection.extend({
  model: Connector,

  initialize: function (models, opts) {
    this.from = opts.wob // A wob has a one-to-many relationship with connectors. Set a reference
                         // back to the wob as `from`, so the connectors can figure out where they originate.

    this.listenTo(this, 'remove', connector => this.from.trigger('connector:remove', connector))
    this.listenTo(this, 'add', connector => {
      this.update() // This will draw the new connector
      this.from.trigger('connector:add', connector) // Indicate a new connector was added on the from wob
    })
    this.listenTo(this, 'change remove', this.update)
    this.listenTo(this, 'change:configurable', this.configurable)
    this.listenTo(this, 'remove', function (connector) {
      this.configurable(connector, false)
    })
    return Backbone.Collection.prototype.initialize.apply(this, arguments)
  },

  update: function () {
    this.from.trigger('change:connectors')
  },

  configurable: function (connector, configurable, intersectionPoint, intersectionOffset) {
    // Trigger this on the `from` wob.
    // Outside code cannot listen to `wob.get('connectors')` events b/c the collection could change
    // based on the canvas history popping.
    this.from.trigger('connectorsConfigurable', connector, configurable, intersectionPoint, intersectionOffset)
  }

})

/*
 * A wob is a 'windowed object', or a 'world of beer' object, whichever you prefer.
 * A wob is any object that is on the canvas. The wob itself does not care what's inside,
 * but rather properties of the object itself, such as its position and size.
 */
var Wob = Backbone.Model.extend({
  idAttribute: '_id',

  defaults: {
    rotate: 0,
    locked: false,
    levitated: false, // Denotes the wob should rise to the top of z-index stack
    connectors: []
  },

  initialize: function () {
    var widgetModel = function (_widget) {
      // If the widget is already a proper Model, just use it
      if (_widget.toJSON) {
        return _widget
      }
      // Otherwise, we need to get the appropriate Model constructor and use
      // the widget as the attributes
      return new this.collection.widgetTypes[_widget.type](_widget, this.collection.options)
    }.bind(this)


    if (this.isNew()) {
      this.widget = widgetModel(this.get('widget'))

      this.listenTo(this, 'saved', function (files) {
        // Once the wob has been created, remove the widget from the backbone attributes so it's not tracked, storing it on the object directly
        this.unset('widget')

        // Tell the widget the wob's id once the wob has an id
        this.widget.set('_id', this.id)
        if (files) {
          this.widget.set('files', files)
        }
      }, this)
    } else {
      // Create the widget backbone model, storing it on the wob
      var _widget = this.get('widget')
      _widget._id = this.id
      this.widget = widgetModel(_widget)
      this.unset('widget')
    }
    this.widget.options = this.collection.options
    this.widget.wob = this

    this.listenTo(this.widget, 'destroy', function (next) {
      // this next callback only comes from overriden widget model in Dash
      next = typeof next === 'function' ? next : _.noop
      this.destroy({
        success: function () { next() },
        error: function (model, xhr, options) { next(new Error(xhr.responseText) )}
      })
    }, this)
  },

  /*
   * Returns all coordinates for this wob
   */
  area: function () {
    return {
      left: this.get('left') || 0,
      top: this.get('top') || 0,
      height: this.get('height') || 0,
      width: this.get('width') || 0
    }
  },

  /*
   * Returns if the wob is flipped based on its rotation angle
   */
  isFlipped: function () {
    let angle = this.get('rotate')

    return (angle >= 45 && angle < 135) || (angle >= 225 && angle < 315)
  },

  /*
   * Returns the center x,y coordinate of the center of the wob
   * This works on rotated wobs, too, since we're always rotating around
   * the center (transform-origin: 50% 50%)
   */
  center: function () {
    return {
      x: this.get('left') + this.get('width') / 2,
      y: this.get('top') + this.get('height') / 2
    }
  },

  /*
   * Returns the bounding box of the wob, adjusting for its rotation
   */
  boundingBox: function () {
    let box = bb(this.area(), this.get('rotate'))

    box.center = box.left + ((box.right - box.left) / 2)
    box.middle = box.top + ((box.bottom - box.top) / 2)

    return box
  },

  destroy: function () {
    this.get('connectors').reset() // Remove all connectors
    this.trigger('predestroy', this)
    this.release()
    Backbone.Model.prototype.destroy.apply(this, arguments)
  },

  getZindex: function () {
    var z = this.get('z-index')
    if (this.get('levitated') && z) {
      z += LEVITATED_OFFSET
    }
    return z
  },

  /**
   * Determines the aspect ratio of a wob. By default this is
   * simply its height / width.
   * However some widgets might want to control the aspect ratio.
   *   e.g. image widgets aspect ratio should be determined by the natural
   *          width/height of the src image
   */
  getAspectRatio: function () {
    if (this.widget.getAspectRatio) {
      // The widget is controlling its aspectRatio
      return this.widget.getAspectRatio()
    } else {
      return this.get('height') / this.get('width')
    }
  },

  /*
   * Resizes the wob based on the given width and height.
   * If either width or height are null, the previous value will be used.
   *
   *  `opts.force` - Force the resize even if the wob is not resizable
   */
  resize: function (width, height, origin = [0, 0], grow, opts = {}) {
    const resizable = opts.force || _.result(this, 'resizable')

    if (!resizable) {
      // Can't resize
      return
    }

    const previous = this.area()
        , minHeight = _.result(this.widget, 'containerDefaults.minHeight', 20)
        , minWidth = _.result(this.widget, 'containerDefaults.minWidth', 20)
        , lockedAR = (grow || _.result(this, 'lockedAR'))
        , angle = this.get('rotate')
        , ratio = opts.ratio || this.getAspectRatio()

    grow = grow || (width == null ? 'width' : 'height')

    if (resizable && typeof resizable === 'object') {
      // Prevent adjust the width if the widget set that on the resizable object
      if (!resizable.width) {
        width = null
      }
      // Same as above, but for height
      if (!resizable.height) {
        height = null
      }
    }

    // If width or height are nil, set them to the previous values, unless there is an angle
    if (_.isNil(width) && !angle) {
      width = previous.width
    }
    if (_.isNil(height) && !angle) {
      height = previous.height
    }

    // Build a box in memory used to calculate the new wob dimensions
    let left = this.get('left') || 0
      , top = this.get('top') || 0
      , x1 = left
      , x2 = left + this.get('width')
      , y1 = top
      , y2 = top + this.get('height')
      , box = new Box(x1, y1, x2, y2)

    box.setMinWidth(minWidth)
    box.setMinHeight(minHeight)

    box.resize(width, height, origin, angle, lockedAR ? grow : null, lockedAR ? ratio : null)

    this.set({
      left: box.x1,
      top: box.y1,
      width: box.width(),
      height: box.height()
    }, opts)

    if (!opts.silent && Object.keys(this.changed).length > 0) {
      // Only trigger a resize event if we changed the wob's dimensions
      this.trigger('resize', this, previous, opts)
    }
  },

  /*
   * Setting levitated: true on a wob will
   * force it to stay in front of other wobs when calculating
   * its z-index
   */
  levitate: function (bool) {
    this.set('levitated', bool)
  },

  setSelected: function (bool, single) {
    if (bool != null) {
      this.selected = bool
      this.trigger('change:selected', this, bool)
    }

    return this.selected
  },

  setConnectorHead: function (bool) {
    if (bool != null) {
      this.trigger('change:connectorHead', this, bool)
    }
  },

  inArea: function (candidate) {
    var area = this.boundingBox()
    return (area.left + area.width) > candidate.left && area.left < (candidate.left + candidate.width) && (area.top + area.height) > candidate.top && area.top < (candidate.top + candidate.height)
  },

  move: function (pos) {
    this.set(_.pick(pos, 'left', 'top'))
    this.trigger('move', this)
  },

  moveBy: function (to) {
    var area = this.area()
    if (to.left) {
      area.left += to.left
      if (this.collection.snapType === SnapType.GRID) {
        area.left = Math.round(area.left / GRID) * GRID
      }
    }
    if (to.top) {
      area.top += to.top
      if (this.collection.snapType === SnapType.GRID) {
        area.top = Math.round(area.top / GRID) * GRID
      }
    }

    this.move({
      left: area.left,
      top: area.top
    })
  },

  release: function () {
    this.stopListening(this)
    if (this.widget) {
      this.widget.trigger('release')
    }
  }
})

var LEVITATED_OFFSET = 10000

export {Wob, Connector, Connectors, LEVITATED_OFFSET}
